import {useMemo, useState} from "react";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {CircularProgress} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {toast} from "sonner";
import {
  Box,
  Collapse,
  Colors,
  CustomDialog,
  Divider,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Text,
} from "@bounce/shared/ui";
import {useGetEmailSpamCheck} from "../../apis/requests/templates/getEmailSpamCheck";
import {useStartEmailSpamCheck} from "../../apis/requests/templates/startEmailSpamCheck";
import {
  EmailDomainType,
  ReachEmailSpamCheckIssue,
  ReachTemplateContentChannel,
  ReachTemplateLanguage,
} from "../../apis/requests/templates/types";
import {TestSendLanguageContent} from "./TestSendDialog";

type Props = {
  accountId: number;
  templateId: string | undefined;
  templateKey: string;
  contentChannel: ReachTemplateContentChannel;
  emailDomainType: EmailDomainType | null;
  contentByLanguage: Partial<Record<ReachTemplateLanguage, TestSendLanguageContent>>;
};

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 24;

const CATEGORY_ORDER = ["spam_rule", "accessibility", "links", "authentication", "html", "content", "blacklist"];

const CATEGORY_LABELS: Record<string, string> = {
  spam_rule: "Spam filter",
  authentication: "Authentication",
  accessibility: "Accessibility",
  html: "HTML",
  content: "Content",
  blacklist: "Blacklist",
  links: "Links",
};

const scoreColor = (score: number | null): string => {
  if (score === null) return Colors.grey._700;
  if (score >= 8) return Colors.green._900;
  if (score >= 5) return Colors.orange._900;
  return Colors.red._900;
};

const groupByCategory = (issues: ReachEmailSpamCheckIssue[]): [string, ReachEmailSpamCheckIssue[]][] => {
  const groups = new Map<string, ReachEmailSpamCheckIssue[]>();
  issues.forEach((issue) => {
    const existing = groups.get(issue.category);
    if (existing) existing.push(issue);
    else groups.set(issue.category, [issue]);
  });
  return [...groups.entries()].sort(
    ([a], [b]) =>
      (CATEGORY_ORDER.indexOf(a) + 1 || CATEGORY_ORDER.length + 1) -
      (CATEGORY_ORDER.indexOf(b) + 1 || CATEGORY_ORDER.length + 1)
  );
};

const CategorySection = ({label, issues}: {label: string; issues: ReachEmailSpamCheckIssue[]}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{borderBottom: `1px solid ${Colors.grey._200}`, width: "100%"}}>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          py: 1,
        }}
      >
        <Text size={14} sx={{fontWeight: 600}}>
          {label}
        </Text>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: Colors.grey._700,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 150ms",
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{pb: 1}}>
          {issues.map((issue, index) => (
            <IssueRow key={`${issue.title}-${index}`} issue={issue} divided={index > 0} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const IssueRow = ({issue, divided}: {issue: ReachEmailSpamCheckIssue; divided: boolean}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
      py: 1,
      minWidth: 0,
      borderTop: divided ? `1px solid ${Colors.grey._200}` : "none",
      "& *": {overflowWrap: "anywhere"},
    }}
  >
    <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1}}>
      <Text size={14} sx={{fontWeight: 600}}>
        {issue.title}
      </Text>
      {issue.score !== null && (
        <Text size={13} sx={{color: Colors.grey._700, whiteSpace: "nowrap"}}>
          {issue.score > 0 ? `+${issue.score}` : issue.score}
        </Text>
      )}
    </Box>
    {issue.description && (
      <Text size={13} sx={{color: Colors.grey._700}}>
        {issue.description}
      </Text>
    )}
    {issue.solution && (
      <Text size={13} sx={{fontWeight: 600}}>
        {issue.solution}
      </Text>
    )}
  </Box>
);

const EmailSpamCheckDialog = NiceModal.create(
  ({accountId, templateId, templateKey, contentChannel, emailDomainType, contentByLanguage}: Props) => {
    const modal = useModal();
    const availableLanguages = useMemo(
      () => (["en", "es"] as ReachTemplateLanguage[]).filter((option) => !!contentByLanguage[option]),
      [contentByLanguage]
    );
    const [language, setLanguage] = useState<ReachTemplateLanguage>("en");

    const start = useStartEmailSpamCheck();
    const started = start.data?.sent ? start.data : null;
    const mailboxId = started?.mailbox_id ?? null;
    const reportUrl = start.data?.report_url ?? null;

    const {data: result} = useGetEmailSpamCheck(
      {mailboxId: mailboxId ?? ""},
      {
        enabled: !!mailboxId,
        refetchInterval: (data, query) => {
          if (data?.ready || data?.error) return false;
          return query.state.dataUpdateCount < MAX_POLLS ? POLL_INTERVAL_MS : false;
        },
      }
    );

    const isReady = !!result?.ready;

    const handleStart = async () => {
      const content = contentByLanguage[language];
      if (!content) return;
      try {
        const response = await start.mutateAsync({
          account_id: accountId,
          template_key: templateKey,
          template_id: templateId ? Number(templateId) : null,
          content_channel: contentChannel,
          email_domain_type: emailDomainType,
          variant: {
            language,
            body: content.body,
            landing_page_body: content.landingPageBody,
            subject: content.subject,
            pre_header: content.preHeader,
            channel_message_code: content.channelMessageCode,
          },
        });
        if (!response.sent) {
          toast.error(response.error ?? "Could not send the test email");
        }
      } catch {
        toast.error("Could not start the spam check");
      }
    };

    const isWaiting = !!mailboxId && !isReady && !result?.error;

    return (
      <CustomDialog
        title="Test for spam"
        forceTitle
        autoClose={false}
        buttonText={mailboxId ? "Close" : start.isLoading ? "Sending…" : "Run check"}
        disabled={start.isLoading}
        onClick={
          mailboxId
            ? () => {
                modal.hide();
              }
            : handleStart
        }
      >
        {!mailboxId && (
          <>
            <Text size={14}>
              Sends this template to a mail-tester address and scores it for spam. Each run uses one mail-tester credit.
            </Text>
            {availableLanguages.length > 1 && (
              <RadioGroup row value={language} onChange={(e) => setLanguage(e.target.value as ReachTemplateLanguage)}>
                {availableLanguages.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={option.toUpperCase()}
                  />
                ))}
              </RadioGroup>
            )}
          </>
        )}

        {isWaiting && (
          <Box sx={{display: "flex", alignItems: "center", gap: 1.5, py: 2}}>
            <CircularProgress size={14} sx={{flexShrink: 0, color: Colors.blue._600}} />
            <Text size={14}>Email sent. Waiting for mail-tester to score it — this can take a minute.</Text>
          </Box>
        )}

        {result?.error && (
          <Text size={14} sx={{color: Colors.red._900}}>
            {result.error}
          </Text>
        )}

        {isReady && result && (
          <Box sx={{display: "flex", flexDirection: "column", gap: 1, width: "100%", minWidth: 0}}>
            <Box sx={{display: "flex", alignItems: "baseline", gap: 1}}>
              <Text size={28} sx={{fontWeight: 700, color: scoreColor(result.score)}}>
                {result.displayed_score ?? "—"}
              </Text>
            </Box>
            <Divider />
            {result.issues.length === 0 ? (
              <Text size={14}>No issues found.</Text>
            ) : (
              <Box sx={{maxHeight: 360, overflowY: "auto", overflowX: "hidden", width: "100%"}}>
                {groupByCategory(result.issues).map(([category, issues]) => (
                  <CategorySection
                    key={category}
                    label={`${CATEGORY_LABELS[category] ?? category} (${issues.length})`}
                    issues={issues}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {reportUrl && (
          <Link href={reportUrl} target="_blank" rel="noreferrer" sx={{fontSize: 13}}>
            View full report on mail-tester
          </Link>
        )}
      </CustomDialog>
    );
  }
);

export default EmailSpamCheckDialog;
